import type { ApiClient } from './client';
import { ApiError, BackendUnreachableError } from './client';
import { buildQueryString } from '@shared/lib/buildQueryString';
import { chatHistoryEnabled, complianceEnabled, useMocks } from '@config/env';
import { mockApi } from './mocks/mockApi';
import { mockComplianceApi } from './mocks/mockComplianceApi';
import type {
  ComplianceAlertDetail,
  ComplianceAlertsPage,
  ComplianceAlertsParams,
  ComplianceCreateAlertRequest,
  ComplianceOverview,
  ComplianceOverviewParams,
  ComplianceUserRisk,
  ComplianceUsersPage,
  ComplianceUsersParams,
} from './complianceTypes';
import type {
  AdminCapabilities,
  AdminDashboard,
  AdminStrategyChat,
  AdminUserDetail,
  AdminUserFillsPage,
  AdminUsersPage,
  AdminPlan,
  AdminCreditPack,
  UpdateCreditPackRequest,
  CreateStripePriceRequest,
  CreateStripePriceResponse,
  PlanStripePrices,
  AiCosts,
  AiCostsParams,
  GrantOverrideRequest,
  GrantOverrideResponse,
  InfraMetrics,
  InfraOverview,
  InfraWindow,
  UpdatePlanRequest,
  UserFillsParams,
  UsersListParams,
} from './adminTypes';

/**
 * Admin API facade. Each method either:
 *   - returns mock data immediately (when `VITE_USE_MOCKS=true`), OR
 *   - calls the real backoffice API and, if the network is UNREACHABLE (DNS/connection refused/
 *     timeout — i.e. the parallel backend isn't up yet), transparently falls back to mocks so the
 *     UI is always demoable. A real HTTP error (4xx/5xx) is NOT swallowed — it surfaces to the UI.
 *
 * The fallback only triggers for genuine "couldn't reach the server" failures (`fetch` rejects with
 * a TypeError), never for a server that answered with an error status (which is a real ApiError).
 */
const BASE = '/api/v1/admin';

function isUnreachable(err: unknown): boolean {
  // `fetch` rejects with a TypeError on DNS failure / connection refused / CORS-preflight network
  // error. An ApiError means the server answered — that's a real error, don't mask it.
  return !(err instanceof ApiError) && err instanceof TypeError;
}

async function withFallback<T>(real: () => Promise<T>, mock: () => Promise<T>): Promise<T> {
  if (useMocks) return mock();
  try {
    return await real();
  } catch (err) {
    if (isUnreachable(err)) {
      console.warn('[adminApi] backend unreachable — serving mock data', err);
      return mock();
    }
    throw err;
  }
}

/**
 * The Compliance console's calls **never fall back to the mock**. `withFallback` keeps the older tabs
 * demoable while the backend is down; for a record of alerts that is the wrong trade: a request that
 * cannot reach the admin API would otherwise paint invented alerts, users and scores as if they were
 * live (a red P1 strip on every page included), and a resolve during an outage would toast "resolved"
 * against a mock that records nothing. So: the mock only in explicit mock mode (`VITE_USE_MOCKS=true`,
 * labelled "sample data" on every console screen); otherwise the real call, and a network failure
 * rejects with `BackendUnreachableError` so the screens say "cannot reach the backend" and the strip
 * shows nothing. An HTTP error surfaces as its `ApiError`, as everywhere else.
 */
async function withoutFallback<T>(real: () => Promise<T>, mock: () => Promise<T>): Promise<T> {
  if (useMocks) return mock();
  try {
    return await real();
  } catch (err) {
    if (isUnreachable(err)) throw new BackendUnreachableError(err);
    throw err;
  }
}

export const adminApi = {
  /** Which schema features the connected backend DB can serve — gates the UI's optional sections. */
  capabilities(api: ApiClient): Promise<AdminCapabilities> {
    return withFallback(
      () => api.get<AdminCapabilities>(`${BASE}/capabilities`),
      () => mockApi.capabilities(),
    );
  },

  dashboard(api: ApiClient, from: string, to: string): Promise<AdminDashboard> {
    return withFallback(
      () => api.get<AdminDashboard>(`${BASE}/dashboard${buildQueryString({ from, to })}`),
      () => mockApi.dashboard(from, to),
    );
  },

  users(api: ApiClient, params: UsersListParams): Promise<AdminUsersPage> {
    return withFallback(
      () => api.get<AdminUsersPage>(`${BASE}/users${buildQueryString(params)}`),
      () => mockApi.users(params),
    );
  },

  /**
   * The deep-dive, with its trading figures narrowed to `accounts` (the page's account picker; empty = every
   * account, sent as no parameter at all). The mock ignores `accounts`: its KPIs are not generated per
   * account, so only the mock fill list narrows — the real backend narrows everything.
   */
  userDetail(
    api: ApiClient, id: string, from: string, to: string, accounts: readonly string[] = [],
  ): Promise<AdminUserDetail> {
    return withFallback(
      () => api.get<AdminUserDetail>(
        `${BASE}/users/${encodeURIComponent(id)}${buildQueryString({ from, to, accounts })}`),
      () => mockApi.userDetail(id, from, to),
    );
  },

  /**
   * One page of a user's raw executions. Its own request rather than part of `userDetail`: an active
   * trader books tens of thousands of fills in a month, and the deep-dive loads on every page view —
   * this is fetched only when the admin opens the fills table.
   */
  userFills(api: ApiClient, id: string, params: UserFillsParams): Promise<AdminUserFillsPage> {
    return withFallback(
      () => api.get<AdminUserFillsPage>(
        `${BASE}/users/${encodeURIComponent(id)}/fills${buildQueryString(params)}`),
      () => mockApi.userFills(id, params),
    );
  },

  /** AI-costs tab: platform totals + daily cost + paged per-user usage (tokens, cost, credit balance). */
  aiCosts(api: ApiClient, params: AiCostsParams): Promise<AiCosts> {
    return withFallback(
      () => api.get<AiCosts>(`${BASE}/ai${buildQueryString(params)}`),
      () => mockApi.aiCosts(params),
    );
  },

  /** Grant an admin tier comp (override) — permanent or one calendar month. Wins over the paid tier. */
  grantOverride(api: ApiClient, id: string, body: GrantOverrideRequest): Promise<GrantOverrideResponse> {
    return withFallback(
      () => api.post<GrantOverrideResponse>(`${BASE}/users/${encodeURIComponent(id)}/subscription-override`, body),
      () => mockApi.grantOverride(id, body),
    );
  },

  /** Revoke the active tier comp, restoring whatever the real subscription entitles. */
  revokeOverride(api: ApiClient, id: string): Promise<void> {
    return withFallback(
      () => api.del<void>(`${BASE}/users/${encodeURIComponent(id)}/subscription-override`),
      () => mockApi.revokeOverride(id),
    );
  },

  /** The plan catalog (`v2_plans`) — the backoffice is its only writer. */
  plans(api: ApiClient): Promise<AdminPlan[]> {
    return withFallback(
      () => api.get<AdminPlan[]>(`${BASE}/plans`),
      () => mockApi.plans(),
    );
  },

  /** Update one plan's configuration (copy, prices, Stripe price ids, credits, flags, ordering). */
  updatePlan(api: ApiClient, tier: string, body: UpdatePlanRequest): Promise<AdminPlan> {
    return withFallback(
      () => api.put<AdminPlan>(`${BASE}/plans/${encodeURIComponent(tier)}`, body),
      () => mockApi.updatePlan(tier, body),
    );
  },

  /** A plan's LIVE Stripe prices beside its stored display cents (drift check). */
  planStripePrices(api: ApiClient, tier: string): Promise<PlanStripePrices> {
    return withFallback(
      () => api.get<PlanStripePrices>(`${BASE}/plans/${encodeURIComponent(tier)}/stripe-prices`),
      () => mockApi.planStripePrices(tier),
    );
  },

  /** Create a NEW Stripe price for one cadence and repoint the plan at it (prices are immutable). */
  createStripePrice(api: ApiClient, tier: string, body: CreateStripePriceRequest): Promise<CreateStripePriceResponse> {
    return withFallback(
      () => api.post<CreateStripePriceResponse>(`${BASE}/plans/${encodeURIComponent(tier)}/stripe-price`, body),
      () => mockApi.createStripePrice(tier, body),
    );
  },

  /** The one-time credit-pack catalog (`v2_credit_packs`) — the backoffice is its only writer. */
  creditPacks(api: ApiClient): Promise<AdminCreditPack[]> {
    return withFallback(
      () => api.get<AdminCreditPack[]>(`${BASE}/credit-packs`),
      () => mockApi.creditPacks(),
    );
  },

  /**
   * Update one credit pack. **This is money and takes effect instantly** — a pack has no Stripe Price
   * object (checkout uses inline `price_data`), so `priceCents` is exactly what the next buyer is charged.
   */
  updateCreditPack(api: ApiClient, code: string, body: UpdateCreditPackRequest): Promise<AdminCreditPack> {
    return withFallback(
      () => api.put<AdminCreditPack>(`${BASE}/credit-packs/${encodeURIComponent(code)}`, body),
      () => mockApi.updateCreditPack(code, body),
    );
  },

  /** Infra tab: status cards + feeds (ECS/ELB/autoscaling + backtest queue health). */
  infra(api: ApiClient): Promise<InfraOverview> {
    return withFallback(
      () => api.get<InfraOverview>(`${BASE}/infra`),
      () => mockApi.infra(),
    );
  },

  /** Infra tab: CloudWatch metric series + backtest run charts for a window. */
  infraMetrics(api: ApiClient, window: InfraWindow): Promise<InfraMetrics> {
    return withFallback(
      () => api.get<InfraMetrics>(`${BASE}/infra/metrics${buildQueryString({ window })}`),
      () => mockApi.infraMetrics(window),
    );
  },

  disableUser(api: ApiClient, id: string): Promise<void> {
    return withFallback(
      () => api.post<void>(`${BASE}/users/${encodeURIComponent(id)}/disable`),
      () => mockApi.disableUser(id),
    );
  },

  enableUser(api: ApiClient, id: string): Promise<void> {
    return withFallback(
      () => api.post<void>(`${BASE}/users/${encodeURIComponent(id)}/enable`),
      () => mockApi.enableUser(id),
    );
  },

  /** Account-sync workflow: mark the user's concurrent login as ESTABLISHED (admin confirmation). */
  markConcurrentLogin(api: ApiClient, id: string): Promise<void> {
    return withFallback(
      () => api.post<void>(`${BASE}/users/${encodeURIComponent(id)}/concurrent-login`),
      () => mockApi.setConcurrentLogin(id, true),
    );
  },

  /** Account-sync workflow: unmark (session torn down) — removes both flags → "No Flag". */
  unmarkConcurrentLogin(api: ApiClient, id: string): Promise<void> {
    return withFallback(
      () => api.del<void>(`${BASE}/users/${encodeURIComponent(id)}/concurrent-login`),
      () => mockApi.setConcurrentLogin(id, false),
    );
  },

  /**
   * One strategy's "Ask the AI" chat turns, oldest first (`v2_ai_chat_turns`). **Dev only** — the
   * caller must check `chatHistoryEnabled` before reaching here.
   *
   * Returns **`null` when the backend doesn't serve this endpoint** (404). This endpoint is not
   * implemented in `journal-backoffice-backend` yet — the contract is defined here so the UI is
   * complete and demoable now, and lights up on its own when the BE ships it. A 404 is the *expected*
   * answer today, so it degrades to a clean "not available" panel rather than an error; every other
   * HTTP status still surfaces as a real error.
   */
  async userStrategyChat(api: ApiClient, userId: string, strategyId: string): Promise<AdminStrategyChat | null> {
    // `chatHistoryEnabled` folds to a literal at build time, so in a prod/demo bundle this returns
    // immediately and everything below is dead code the optimiser removes. Such a build therefore
    // cannot issue this request at all — not from a stray caller, not from the console. The hook
    // checks the same flag; this is the second, independent check.
    if (!chatHistoryEnabled) return null;
    return withFallback(
      async () => {
        try {
          return await api.get<AdminStrategyChat>(
            `${BASE}/users/${encodeURIComponent(userId)}/strategies/${encodeURIComponent(strategyId)}/chat`,
          );
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }
      },
      () => mockApi.userStrategyChat(userId, strategyId),
    );
  },

  /** AI Backtester: grant extra prompt credits (one AdminGrant ledger row). Returns the new balance. */
  addAiCredits(api: ApiClient, id: string, amount: number): Promise<{ balance: number }> {
    return withFallback(
      () => api.post<{ balance: number }>(`${BASE}/users/${encodeURIComponent(id)}/credits`, { amount }),
      () => mockApi.addAiCredits(id, amount),
    );
  },

  // ─── Compliance console (`/compliance/*`, content-blind; contract in complianceTypes.ts) ───
  //
  // Every method below refuses to run unless `complianceEnabled` — the same second, independent check
  // the chat reader has. The flag folds to a literal at build time, so a prod/demo bundle cannot issue
  // any of these requests, not from a stray caller and not from the console.
  //
  // And every method below goes through `withoutFallback`, never `withFallback`: an unreachable backend
  // is an error state on these screens, never a mock (see the helper's comment).

  /** The alerts inbox: one page, newest first, with the status counts and the store-wide open P1 count. */
  complianceAlerts(api: ApiClient, params: ComplianceAlertsParams): Promise<ComplianceAlertsPage> {
    if (!complianceEnabled) return Promise.reject(complianceOff());
    return withoutFallback(
      () => api.get<ComplianceAlertsPage>(`${COMPLIANCE}/alerts${buildQueryString(params)}`),
      () => mockComplianceApi.complianceAlerts(params),
    );
  },

  /** One alert with its reduced payload, raw message hash, evidence links and event history. */
  complianceAlert(api: ApiClient, id: string): Promise<ComplianceAlertDetail> {
    if (!complianceEnabled) return Promise.reject(complianceOff());
    return withoutFallback(
      () => api.get<ComplianceAlertDetail>(`${COMPLIANCE}/alerts/${encodeURIComponent(id)}`),
      () => mockComplianceApi.complianceAlert(id),
    );
  },

  /** Any admin. Appends an `acknowledged` event. */
  acknowledgeAlert(api: ApiClient, id: string): Promise<ComplianceAlertDetail> {
    if (!complianceEnabled) return Promise.reject(complianceOff());
    return withoutFallback(
      () => api.post<ComplianceAlertDetail>(`${COMPLIANCE}/alerts/${encodeURIComponent(id)}/acknowledge`),
      () => mockComplianceApi.acknowledgeAlert(id),
    );
  },

  /** Note required. The backend answers 403 for a non-supervisor on an AI-content alert (D14). */
  resolveAlert(api: ApiClient, id: string, note: string): Promise<ComplianceAlertDetail> {
    if (!complianceEnabled) return Promise.reject(complianceOff());
    return withoutFallback(
      () => api.post<ComplianceAlertDetail>(`${COMPLIANCE}/alerts/${encodeURIComponent(id)}/resolve`, { note }),
      () => mockComplianceApi.resolveAlert(id, note),
    );
  },

  /** Note required. Returns the alert to `acknowledged`. */
  reopenAlert(api: ApiClient, id: string, note: string): Promise<ComplianceAlertDetail> {
    if (!complianceEnabled) return Promise.reject(complianceOff());
    return withoutFallback(
      () => api.post<ComplianceAlertDetail>(`${COMPLIANCE}/alerts/${encodeURIComponent(id)}/reopen`, { note }),
      () => mockComplianceApi.reopenAlert(id, note),
    );
  },

  /** A `noted` event; never changes the status. */
  noteAlert(api: ApiClient, id: string, note: string): Promise<ComplianceAlertDetail> {
    if (!complianceEnabled) return Promise.reject(complianceOff());
    return withoutFallback(
      () => api.post<ComplianceAlertDetail>(`${COMPLIANCE}/alerts/${encodeURIComponent(id)}/notes`, { note }),
      () => mockComplianceApi.noteAlert(id, note),
    );
  },

  /** A manual alert (any admin) or the monthly `audit-escalation` (supervisor; the backend enforces it). */
  createAlert(api: ApiClient, body: ComplianceCreateAlertRequest): Promise<ComplianceAlertDetail> {
    if (!complianceEnabled) return Promise.reject(complianceOff());
    return withoutFallback(
      () => api.post<ComplianceAlertDetail>(`${COMPLIANCE}/alerts`, body),
      () => mockComplianceApi.createAlert(body),
    );
  },

  /** The risk overview: header counts, per-surface and per-rule stats, environment checks, recent alerts. */
  complianceOverview(api: ApiClient, params: ComplianceOverviewParams = {}): Promise<ComplianceOverview> {
    if (!complianceEnabled) return Promise.reject(complianceOff());
    return withoutFallback(
      () => api.get<ComplianceOverview>(`${COMPLIANCE}/overview${buildQueryString({ ...params })}`),
      () => mockComplianceApi.complianceOverview(params),
    );
  },

  /** The ranked users (score orders; the signals are what is shown). */
  complianceUsers(api: ApiClient, params: ComplianceUsersParams): Promise<ComplianceUsersPage> {
    if (!complianceEnabled) return Promise.reject(complianceOff());
    return withoutFallback(
      () => api.get<ComplianceUsersPage>(`${COMPLIANCE}/users${buildQueryString(params)}`),
      () => mockComplianceApi.complianceUsers(params),
    );
  },

  /** One user's signals, timeline of ids and dates, acceptances and open alerts. 404 = unknown user. */
  complianceUser(api: ApiClient, id: string): Promise<ComplianceUserRisk> {
    if (!complianceEnabled) return Promise.reject(complianceOff());
    return withoutFallback(
      () => api.get<ComplianceUserRisk>(`${COMPLIANCE}/users/${encodeURIComponent(id)}`),
      () => mockComplianceApi.complianceUser(id),
    );
  },

  /**
   * "Find a trader" on the console (display plan veto item 12): the admin users list searched by email, through
   * `withoutFallback` like every console call, so an unreachable backend is an error, never sample traders.
   */
  complianceFindTraders(api: ApiClient, params: UsersListParams): Promise<AdminUsersPage> {
    if (!complianceEnabled) return Promise.reject(complianceOff());
    return withoutFallback(
      () => api.get<AdminUsersPage>(`${BASE}/users${buildQueryString(params)}`),
      () => mockApi.users(params),
    );
  },

  /** "Mark reviewed": one resolved `user-reviewed` record in the inbox carrying the note (never an account action). */
  markUserReviewed(api: ApiClient, id: string, note: string): Promise<void> {
    if (!complianceEnabled) return Promise.reject(complianceOff());
    return withoutFallback(
      () => api.post<void>(`${COMPLIANCE}/users/${encodeURIComponent(id)}/reviewed`, { note }),
      () => mockComplianceApi.markUserReviewed(id, note),
    );
  },
};

const COMPLIANCE = `${BASE}/compliance`;

function complianceOff(): Error {
  return new Error('The Compliance console is not enabled in this build.');
}
